// Product placement editor: put a company-profile product into an existing
// image by dragging and resizing a box on it. Opened from the image picker's
// "Place a product" action (editor/src/index.js).
//
// The box is the instruction. Its position and size, as fractions of the
// image AS DISPLAYED UPRIGHT, go to POST /reach/hero/place-product; the server
// draws the guide outline and tells the model which product and how big. The
// customer never writes a size or a position.
//
// This is the one drag-to-place primitive (docs/CANONICAL_SYSTEMS.md): the
// next feature that positions something on an image reuses it, it does not
// grow a second one. The geometry is exported as pure functions so it can be
// tested without a DOM (tests/js/product-placement-geometry.test.cjs).
//
// All server text (product names, messages) is written with textContent.

// Smallest side of the box, as a fraction of the image. The server enforces
// the same floor (ADAMIRO_Product_Placement); a smaller box is a speck.
export const MIN_SIDE = 0.03;
// Width of a new box as a fraction of the image width.
const START_WIDTH = 0.25;
// Arrow key step, as a fraction of the image.
const KEY_STEP = 0.01;

function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
}

// pA = product photo aspect (width / height), iA = image aspect. A box that
// is w wide (fraction of image width) is h = w * iA / pA tall (fraction of
// image height), so the product keeps its own shape on screen.
export function boxHeight(w, pA, iA) {
    return (w * iA) / pA;
}

export function widthLimits(pA, iA) {
    var maxW = Math.min(1, pA / iA);
    var minW = Math.max(MIN_SIDE, (MIN_SIDE * pA) / iA);
    if (minW > maxW) { minW = maxW; }
    return { minW: minW, maxW: maxW };
}

// Keep a box inside the image, with the product's aspect. Height is always
// derived from width, never trusted from the input.
export function fitBox(box, pA, iA) {
    var lim = widthLimits(pA, iA);
    var w = clamp(box.w, lim.minW, lim.maxW);
    var h = boxHeight(w, pA, iA);
    return {
        x: clamp(box.x, 0, Math.max(0, 1 - w)),
        y: clamp(box.y, 0, Math.max(0, 1 - h)),
        w: w,
        h: h,
    };
}

// A new box: centred, a quarter of the image wide, the product's shape.
export function initialBox(pA, iA) {
    var lim = widthLimits(pA, iA);
    var w = clamp(START_WIDTH, lim.minW, lim.maxW);
    var h = boxHeight(w, pA, iA);
    // A very tall product at a quarter width would be taller than the image.
    if (h > 0.9) {
        h = 0.9;
        w = (h * pA) / iA;
    }
    return fitBox({ x: (1 - w) / 2, y: (1 - h) / 2, w: w, h: h }, pA, iA);
}

// Move by a fraction of the image in each axis.
export function moveBox(start, dx, dy, pA, iA) {
    return fitBox({ x: start.x + dx, y: start.y + dy, w: start.w, h: start.h }, pA, iA);
}

// Corner-handle drag: the top-left corner stays put, the bottom-right follows
// the pointer. The width follows whichever axis the pointer moved further
// along (in units of the box's own shape), and never grows past the edge.
export function handleResize(start, dx, dy, pA, iA) {
    var dyAsW = (dy * pA) / iA;
    var dw = Math.abs(dx) >= Math.abs(dyAsW) ? dx : dyAsW;
    var maxW = Math.min(1 - start.x, ((1 - start.y) * pA) / iA);
    var w = Math.min(maxW, start.w + dw);
    return fitBox({ x: start.x, y: start.y, w: w, h: start.h }, pA, iA);
}

// Keyboard resize: grow or shrink around the centre by dw (fraction of the
// image width), then pull back inside the image.
export function resizeAroundCentre(start, dw, pA, iA) {
    var lim = widthLimits(pA, iA);
    var w = clamp(start.w + dw, lim.minW, lim.maxW);
    var h = boxHeight(w, pA, iA);
    return fitBox({
        x: start.x - (w - start.w) / 2,
        y: start.y - (h - start.h) / 2,
        w: w,
        h: h,
    }, pA, iA);
}

function round4(n) {
    return Math.round(n * 10000) / 10000;
}

// The box as sent to the server: four decimals, never poking past 1.
export function serializeBox(box) {
    var w = round4(box.w);
    var h = round4(box.h);
    return {
        x: Math.min(round4(box.x), round4(1 - w)),
        y: Math.min(round4(box.y), round4(1 - h)),
        w: w,
        h: h,
    };
}

function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) { node.className = className; }
    if (text) { node.textContent = text; }
    return node;
}

var openInstance = null;

// openProductPlacement({
//   imageUrl, attachmentId, products, fetchFn, sessionId, draftId, onPlaced,
//   host, companyUrl
// })
//   products   [{id, name, height_cm, thumb_url, image_url}] from GET /reach/products
//   fetchFn    (path, body) => Promise<{ok, status, data}>, e.g. the picker's reachFetch
//   onPlaced   called with the response data of a successful placement
//   host       element the editor covers (the picker card); default document.body
//   companyUrl where a product photo is added; shown when there are no products
// Returns { close }.
export function openProductPlacement(opts) {
    opts = opts || {};
    if (openInstance) { openInstance.close(); }

    var products = Array.isArray(opts.products) ? opts.products : [];
    var host = opts.host || document.body;
    var restoreFocus = document.activeElement;

    var state = {
        product: null, // chosen product object
        pA: 1, // product photo aspect
        iA: 1, // image aspect
        box: null, // {x, y, w, h} fractions
        busy: false,
        imageReady: false,
        closed: false,
    };
    var drag = null;

    // ---- DOM -------------------------------------------------------------
    var root = el('div', 'adm-pp' + (host === document.body ? ' adm-pp--fixed' : ''));
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'adm-pp-title');

    var head = el('div', 'adm-pp__head');
    var title = el('h3', 'adm-pp__title', 'Place a product');
    title.id = 'adm-pp-title';
    var closeX = el('button', 'adm-media-picker__close', '×');
    closeX.type = 'button';
    closeX.setAttribute('aria-label', 'Close');
    head.appendChild(title);
    head.appendChild(closeX);

    var stage = el('div', 'adm-pp__stage');
    var frame = el('div', 'adm-pp__frame');
    var img = el('img', 'adm-pp__img');
    img.alt = '';
    img.draggable = false;
    frame.appendChild(img);

    var box = el('div', 'adm-pp__box');
    box.tabIndex = 0;
    box.setAttribute('role', 'group');
    box.setAttribute('aria-label', 'Product position and size. Arrow keys move it, shift and arrow keys resize it.');
    box.hidden = true;
    var boxImg = el('img', 'adm-pp__box-img');
    boxImg.alt = '';
    boxImg.draggable = false;
    var handle = el('span', 'adm-pp__handle');
    handle.setAttribute('aria-hidden', 'true');
    box.appendChild(boxImg);
    box.appendChild(handle);
    frame.appendChild(box);
    stage.appendChild(frame);

    var hint = el('p', 'adm-pp__hint', 'Pick a product, drag the box to where it goes and pull the corner to set its size.');

    var strip = el('div', 'adm-pp__strip');
    strip.setAttribute('role', 'group');
    strip.setAttribute('aria-label', 'Product');
    var chips = [];
    products.forEach(function (p) {
        var chip = el('button', 'adm-mp-gen__aspect-btn adm-mp-gen__product-chip');
        chip.type = 'button';
        chip.setAttribute('aria-pressed', 'false');
        chip.disabled = true; // until the image has loaded
        var thumb = el('img', 'adm-mp-gen__product-thumb');
        thumb.alt = '';
        thumb.src = p.thumb_url || p.image_url || '';
        var name = el('span', '', p.name || 'Product');
        chip.appendChild(thumb);
        chip.appendChild(name);
        chip.addEventListener('click', function () { pickProduct(p, chip); });
        strip.appendChild(chip);
        chips.push({ product: p, node: chip });
    });
    var empty = null;
    if (!products.length) {
        if (opts.companyUrl) {
            empty = el('a', 'adm-pp__empty', 'Add a product photo in your company profile');
            empty.href = opts.companyUrl;
            empty.target = '_blank';
            empty.rel = 'noopener';
        } else {
            empty = el('p', 'adm-pp__empty', 'Add a product photo in your company profile');
        }
        strip.appendChild(empty);
    }

    var note = el('input', 'adm-media-picker__alt adm-pp__note');
    note.type = 'text';
    note.maxLength = 200;
    note.placeholder = 'Anything else? e.g. in the nurse\'s hand';
    note.setAttribute('aria-label', 'Anything else?');

    var status = el('div', 'adm-mp-gen__status adm-pp__status');
    status.hidden = true;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    var actions = el('div', 'adm-pp__actions');
    var cancelBtn = el('button', 'adm-media-picker__btn', 'Cancel');
    cancelBtn.type = 'button';
    var placeBtn = el('button', 'adm-media-picker__btn adm-media-picker__btn--primary', 'Place product');
    placeBtn.type = 'button';
    placeBtn.disabled = true;
    actions.appendChild(cancelBtn);
    actions.appendChild(placeBtn);

    root.appendChild(head);
    root.appendChild(stage);
    root.appendChild(hint);
    root.appendChild(strip);
    root.appendChild(note);
    root.appendChild(status);
    root.appendChild(actions);
    host.appendChild(root);

    // ---- State helpers -----------------------------------------------------
    function setStatus(msg, isError) {
        if (!msg) { status.hidden = true; status.textContent = ''; status.classList.remove('is-error'); return; }
        status.hidden = false;
        status.textContent = msg;
        status.classList.toggle('is-error', !!isError);
    }

    function refresh() {
        placeBtn.disabled = state.busy || !state.product || !state.box || !opts.attachmentId;
        cancelBtn.disabled = state.busy;
        closeX.disabled = state.busy;
        note.disabled = state.busy;
        chips.forEach(function (c) {
            c.node.disabled = state.busy || !state.imageReady;
        });
        root.classList.toggle('is-busy', state.busy);
        root.setAttribute('aria-busy', state.busy ? 'true' : 'false');
    }

    function paintBox() {
        if (!state.box) { return; }
        box.style.left = (state.box.x * 100) + '%';
        box.style.top = (state.box.y * 100) + '%';
        box.style.width = (state.box.w * 100) + '%';
        box.style.height = (state.box.h * 100) + '%';
    }

    function pickProduct(p, chipNode) {
        if (state.busy || !state.imageReady) { return; }
        setStatus(null);
        chips.forEach(function (c) {
            c.node.setAttribute('aria-pressed', c.node === chipNode ? 'true' : 'false');
            c.node.classList.toggle('is-selected', c.node === chipNode);
        });
        state.product = p;
        // Measure the photo that goes in the box: the box takes its shape.
        var probe = new Image();
        probe.onload = function () {
            if (state.closed || state.product !== p) { return; }
            state.pA = (probe.naturalWidth && probe.naturalHeight) ? probe.naturalWidth / probe.naturalHeight : 1;
            state.box = initialBox(state.pA, state.iA);
            boxImg.src = probe.src;
            box.hidden = false;
            paintBox();
            refresh();
            box.focus({ preventScroll: true });
        };
        probe.onerror = function () {
            if (state.closed || state.product !== p) { return; }
            state.product = null;
            chipNode.setAttribute('aria-pressed', 'false');
            chipNode.classList.remove('is-selected');
            setStatus('That product photo could not be loaded.', true);
            refresh();
        };
        probe.src = p.image_url || p.thumb_url || '';
    }

    // ---- Pointer drag and resize --------------------------------------------
    function bindDrag(target, mode) {
        target.addEventListener('pointerdown', function (ev) {
            if (state.busy || !state.box) { return; }
            if (ev.pointerType === 'mouse' && ev.button !== 0) { return; }
            ev.preventDefault();
            ev.stopPropagation();
            try { target.setPointerCapture(ev.pointerId); } catch (e) { /* synthetic event */ }
            var rect = frame.getBoundingClientRect();
            drag = {
                id: ev.pointerId,
                mode: mode,
                sx: ev.clientX,
                sy: ev.clientY,
                w: rect.width || 1,
                h: rect.height || 1,
                start: { x: state.box.x, y: state.box.y, w: state.box.w, h: state.box.h },
            };
            box.classList.add('is-dragging');
            if (mode === 'move') { box.focus({ preventScroll: true }); }
        });
        target.addEventListener('pointermove', function (ev) {
            if (!drag || drag.id !== ev.pointerId || drag.mode !== mode) { return; }
            var dx = (ev.clientX - drag.sx) / drag.w;
            var dy = (ev.clientY - drag.sy) / drag.h;
            state.box = mode === 'move'
                ? moveBox(drag.start, dx, dy, state.pA, state.iA)
                : handleResize(drag.start, dx, dy, state.pA, state.iA);
            paintBox();
        });
        function end(ev) {
            if (!drag || drag.id !== ev.pointerId) { return; }
            drag = null;
            box.classList.remove('is-dragging');
            try { target.releasePointerCapture(ev.pointerId); } catch (e) { /* already released */ }
        }
        target.addEventListener('pointerup', end);
        target.addEventListener('pointercancel', end);
    }
    bindDrag(box, 'move');
    bindDrag(handle, 'resize');

    // ---- Keyboard ------------------------------------------------------------
    box.addEventListener('keydown', function (ev) {
        if (state.busy || !state.box) { return; }
        var dx = 0;
        var dy = 0;
        switch (ev.key) {
            case 'ArrowLeft': dx = -KEY_STEP; break;
            case 'ArrowRight': dx = KEY_STEP; break;
            case 'ArrowUp': dy = -KEY_STEP; break;
            case 'ArrowDown': dy = KEY_STEP; break;
            default: return;
        }
        ev.preventDefault();
        if (ev.shiftKey) {
            // Right/Down grow, Left/Up shrink; the width moves 1 % of the image.
            state.box = resizeAroundCentre(state.box, dx + dy, state.pA, state.iA);
        } else {
            state.box = moveBox(state.box, dx, dy, state.pA, state.iA);
        }
        paintBox();
    });

    // Escape closes the editor, not the picker under it. Captured on the
    // document so it runs before the picker's own Escape handler and keeps it
    // from also firing.
    function onDocKey(ev) {
        // The picker can be removed with this editor still inside it. A
        // listener that outlives its DOM would swallow every Escape on the page.
        if (!root.isConnected) { close(); return; }
        if (ev.key === 'Escape') {
            ev.preventDefault();
            ev.stopPropagation();
            if (!state.busy) { close(); }
            return;
        }
        if (ev.key === 'Tab') {
            var items = root.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex="0"]');
            var list = Array.prototype.filter.call(items, function (n) { return !n.hidden && n.offsetParent !== null; });
            if (!list.length) { return; }
            var first = list[0];
            var last = list[list.length - 1];
            if (!root.contains(document.activeElement)) {
                ev.preventDefault();
                first.focus();
            } else if (ev.shiftKey && document.activeElement === first) {
                ev.preventDefault();
                last.focus();
            } else if (!ev.shiftKey && document.activeElement === last) {
                ev.preventDefault();
                first.focus();
            }
        }
    }
    document.addEventListener('keydown', onDocKey, true);

    // ---- Close / submit -------------------------------------------------------
    function close() {
        if (state.closed) { return; }
        state.closed = true;
        document.removeEventListener('keydown', onDocKey, true);
        if (root.parentNode) { root.parentNode.removeChild(root); }
        if (openInstance === api) { openInstance = null; }
        if (restoreFocus && typeof restoreFocus.focus === 'function' && document.contains(restoreFocus)) {
            restoreFocus.focus({ preventScroll: true });
        }
    }
    closeX.addEventListener('click', function () { if (!state.busy) { close(); } });
    cancelBtn.addEventListener('click', function () { if (!state.busy) { close(); } });

    placeBtn.addEventListener('click', function () {
        if (state.busy || !state.product || !state.box || !opts.attachmentId) { return; }
        if (typeof opts.fetchFn !== 'function') { return; }
        var body = {
            base_attachment_id: opts.attachmentId,
            product_id: state.product.id,
            box: serializeBox(state.box),
        };
        var noteText = (note.value || '').trim();
        if (noteText) { body.note = noteText; }
        if (opts.sessionId) { body.session_id = opts.sessionId; }
        if (opts.draftId) { body.draft_id = opts.draftId; }

        state.busy = true;
        refresh();
        setStatus('Placing the product. This usually takes 10 to 20 seconds.', false);
        opts.fetchFn('hero/place-product', body).then(function (resp) {
            if (state.closed) { return; }
            state.busy = false;
            if (!resp || !resp.ok || !resp.data || !resp.data.success) {
                setStatus((resp && resp.data && resp.data.message) || 'Could not place the product. Try again.', true);
                refresh();
                return;
            }
            var data = resp.data;
            close();
            if (typeof opts.onPlaced === 'function') { opts.onPlaced(data); }
        }).catch(function () {
            if (state.closed) { return; }
            state.busy = false;
            setStatus('Network error placing the product. Try again.', true);
            refresh();
        });
    });

    // ---- Image -----------------------------------------------------------------
    img.addEventListener('load', function () {
        if (state.closed) { return; }
        state.iA = (img.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : 1;
        state.imageReady = true;
        refresh();
        var firstChip = chips.length ? chips[0].node : null;
        if (firstChip) { firstChip.focus({ preventScroll: true }); } else { closeX.focus({ preventScroll: true }); }
    });
    img.addEventListener('error', function () {
        if (state.closed) { return; }
        setStatus('The image could not be loaded.', true);
        refresh();
    });
    img.src = opts.imageUrl || '';
    refresh();
    closeX.focus({ preventScroll: true });

    var api = { close: close };
    openInstance = api;
    return api;
}
